/*
 * File: LegalSnippet.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 9:53 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

// Components
import { Typography } from '../Typography';
import { LocaleLink } from '../LocaleLink';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './LegalSnippet.styles';


/**
 * Component
 */

export const LegalSnippet = ({
	className, context
}) => {

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get legal urls
	const termsUrl = useMemo(() => (partner?.legal?.links?.terms ? partner?.legal?.links?.terms : `https://www.ourwave.org${locale.localePath}/terms`), [partner, locale]);
	const privacyUrl = useMemo(() => (partner?.legal?.links?.privacy ? partner?.legal?.links?.privacy : `https://www.ourwave.org${locale.localePath}/privacy`), [partner, locale]);
	const reviewGuidelinesUrl = useMemo(() => (partner?.legal?.links?.review_guidelines ? partner?.legal?.links?.review_guidelines : `https://www.ourwave.org${locale.localePath}/review-guidelines`), [partner, locale]);
	const communityGuidelinesUrl = useMemo(() => (partner?.legal?.links?.community_guidelines ? partner?.legal?.links?.community_guidelines : `https://www.ourwave.org${locale.localePath}/community-guidelines`), [partner, locale]);
	const securityUrl = useMemo(() => (partner?.legal?.links?.security ? partner?.legal?.links?.security : `https://www.ourwave.org${locale.localePath}/security`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	if (context === 'message') {
		return (
			<S.Wrapper className={className}>
				<Typography tag="p" variation="4">
					<Trans t={t} i18nKey="legalSnippet.agreeCommunityGuidelinesMessage">
						Please adhere to our
						{' '}
						<LocaleLink to={communityGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Community Guidelines</LocaleLink>
						{' '}
						to help us keep
						{' '}
						{{ partnerName: partner.name }}
						{' '}
						a safe space. All messages will be reviewed and identifying information removed before they are posted.
					</Trans>
				</Typography>
			</S.Wrapper>
		);
	} if (context === 'comment') {
		<S.Wrapper className={className}>
			<Typography tag="p" variation="4">
				<Trans t={t} i18nKey="legalSnippet.agreeCommunityGuidelinesComment">
					Please adhere to our
					{' '}
					<LocaleLink to={communityGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Community Guidelines</LocaleLink>
					{' '}
					to help us keep
					{' '}
					{{ partnerName: partner.name }}
					{' '}
					a safe space.
				</Trans>
			</Typography>
		</S.Wrapper>;
	}
	return (
		<S.Wrapper className={className}>
			<Typography tag="p" variation="3">
				{context === 'story' ? (
					<Trans t={t} i18nKey="legalSnippet.agreeTermsPrivacyStory">
						By sharing your story with
						{' '}
						{{ partnerName: partner.name }}
						, you agree to our
						{' '}
						<LocaleLink to={termsUrl} target={!hasInternalLegalPages ? '_blank' : null}>Terms of Service</LocaleLink>
						{' '}
						and
						{' '}
						<LocaleLink to={privacyUrl} target={!hasInternalLegalPages ? '_blank' : null}>Privacy Policy</LocaleLink>
						.
					</Trans>
				) : (
					<Trans t={t} i18nKey="legalSnippet.agreeTermsPrivacyUpdate">
						By sharing your story update with
						{' '}
						{{ partnerName: partner.name }}
						, you agree to our
						{' '}
						<LocaleLink to={termsUrl} target={!hasInternalLegalPages ? '_blank' : null}>Terms of Service</LocaleLink>
						{' '}
						and
						{' '}
						<LocaleLink to={privacyUrl} target={!hasInternalLegalPages ? '_blank' : null}>Privacy Policy</LocaleLink>
						.
					</Trans>
				)}
			</Typography>
			<Typography tag="p" variation="3">
				{context === 'story' ? (
					<Trans t={t} i18nKey="legalSnippet.reviewStory">
						All stories will be reviewed and all identifying information removed before they are posted according to our
						{' '}
						<LocaleLink to={reviewGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Review Guidelines</LocaleLink>
						.
					</Trans>
				) : (
					<Trans t={t} i18nKey="legalSnippet.reviewUpdate">
						All updates will be reviewed and all identifying information removed before they are posted according to our
						{' '}
						<LocaleLink to={reviewGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Review Guidelines</LocaleLink>
						.
					</Trans>
				)}
			</Typography>
			<Typography tag="p" variation="3">
				<Trans t={t} i18nKey="legalSnippet.security">
					<LocaleLink to={securityUrl} target={!hasInternalLegalPages ? '_blank' : null}>Learn More</LocaleLink>
					{' '}
					about our security.
				</Trans>
			</Typography>
		</S.Wrapper>
	);
};


/**
 * Configuration
 */

LegalSnippet.displayName = 'LegalSnippet';
LegalSnippet.propTypes = {
	className: PropTypes.string,
	context: PropTypes.oneOf(['story', 'update', 'message', 'comment']),
};
LegalSnippet.defaultProps = {
	className: null,
	context: 'story',
};
